import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useUser, { useUserForm } from '@hooks/useUser'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'CreateOrEditUserModal'

type ModalCallbacks = {
  closeModal: () => void,
  createUser: (payload: HandleCallbackActionParams) => void,
  updateUser: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditUserModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createUser, updateUser } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const user = digObject(modalPayload, 'user', {})

  const newRecord = !user.id

  const {
    callbacks: {
      createUser: createFn,
      updateUser: updateFn,
    },
    creating,
    updating,
  } = useUser(user)

  const {
    entityState,
    entityState: {
      title,
    },
    errors,
    handlers,
    saveEnabled,
  } = useUserForm(user, { validateOn: 'change' })

  const createOrUpdateUserPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: ` ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={newRecord ? 'Create' : 'Edit'} titleSecondLine="" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createUser(createOrUpdateUserPayload)
              : () => updateUser(createOrUpdateUserPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} ` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditUserModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
